import * as React from 'react';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Typography, Box, Container, TextField, CssBaseline, Button, Avatar, Select, MenuItem, FormHelperText, InputLabel, CircularProgress, FormControl, Tabs, Tab, Card, CardHeader, CardMedia, IconButton, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, Tooltip } from '@mui/material';
import { connect } from "react-redux";
import { Navigate, NavLink, Route, Routes, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { centered_flex_box, MainInput, MainInputLabel, MainPassword, main_button, StyledInput, MainPhone, sec_button, left_flex_box } from '../../app/components/Styles';
import { brandFilterOptions, brandOptionLabel } from '../../app/components/BrandSelect';
import { isMobile } from 'react-device-detect';
import { getVendors, addOutbound, getProducts, fulfillBazaarOutbound } from '../../app/store/actions/imsAdminActions.js';
import { Close, Delete, Download, Upload } from '@mui/icons-material';
import { useEffect } from 'react';
import axios from 'axios';
import ReactFileReader from 'react-file-reader';
import { notification } from 'antd';
import download from 'downloadjs';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { enrichStockItemsFromProducts, mapPrefilledOutboundProducts } from './bazaarOutboundPrefill';

const sortProductsBySku = (productList) => {
    return [...(productList || [])].sort((a, b) => {
        const skuA = (a.sku || a.externalId || '').toString().toLowerCase();
        const skuB = (b.sku || b.externalId || '').toString().toLowerCase();
        return skuA.localeCompare(skuB);
    });
};

export const CreateOutbound = ({ addOutbound, getVendors, getProducts, fulfillBazaarOutbound, products, vendors, isLoading, user, token }) => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const bazaarId = searchParams.get('bazaarId');
    const [stockItems, setStockItems] = React.useState([]);
    const [vendorId, setVendorId] = React.useState(null);
    const [file, setFile] = React.useState(null);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [notes, setNotes] = React.useState('');
    const [refreshDialogOpen, setRefreshDialogOpen] = React.useState(false);
    const [refreshPassword, setRefreshPassword] = React.useState('');
    const [refreshError, setRefreshError] = React.useState(null);
    const [refreshValidated, setRefreshValidated] = React.useState(false);
    const [refreshLoading, setRefreshLoading] = React.useState(false);
    const [pendingSubmission, setPendingSubmission] = React.useState(null);
    const [addFees, setAddFees] = React.useState(true);

    const requiresRefreshPassword = React.useMemo(() => ["65cba5b59d76a5203fb0cdae", "68fea2723820930efc7db2b5", "685ea490b34528b6f3841909"].includes(user?._id), [user]);

    const selectedVendor = React.useMemo(
        () => vendors?.find((vendor) => vendor._id === vendorId),
        [vendors, vendorId]
    );

    const handleFiles = (files) => {
        setFile(files[0]);
    };

    useEffect(() => {
        if (!file || !products?.length) {
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                const header = parsedData[0];
                const items = [];
                parsedData.slice(1).forEach((row) => {
                    const item = {};
                    row.forEach((value, index) => {
                        item[header[index]] = value;
                    });
                    const found = products.find(
                        (product) => product.sku === item["Product SKU"] || product.externalId === item["Product SKU"]
                    );
                    const requestedQty = parseInt(item["Quantity"], 10);
                    const maxQty = found?.stock?.amount ?? 0;
                    if (found && !found.deletedFromShopify && requestedQty > 0 && maxQty > 0) {
                        items.push({
                            _id: found._id,
                            sku: found.sku || found.externalId,
                            name: found.name,
                            quantity: Math.min(requestedQty, maxQty),
                            stock: found.stock,
                            size: found.size,
                            color: found.color,
                            weight: found.weight,
                        });
                    }
                });
                setStockItems(items);
            } catch (err) {
                console.log(err);
                notification.error({ message: 'Unable to read Excel file. Please use the downloaded template.' });
            }
        };
        reader.readAsArrayBuffer(file);
    }, [file, products]);

    const handleExportToExcel = async () => {
        if (!vendorId || !products?.length) {
            notification.warning({ message: 'Select a brand and wait for products to load before downloading.' });
            return;
        }
        setIsDownloading(true);
        notification.info({ message: 'Downloading outbound product template...' });
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Outbound Products');
        workbook.creator = 'Chainz';

        worksheet.columns = [
            { header: 'Product SKU', key: 'sku' },
            { header: 'Product Name', key: 'name' },
            { header: 'Quantity', key: 'quantity' },
        ];

        sortProductsBySku(products)
            .filter((product) => !product.deletedFromShopify)
            .forEach((product) => {
                worksheet.addRow({
                    sku: product.sku || product.externalId,
                    name: product.name,
                    quantity: 0,
                });
            });

        const buffer = await workbook.xlsx.writeBuffer();
        const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const brandName = selectedVendor?.brandName || 'Outbound';
        const fileName = `${brandName} Outbound - ${moment().format('DD_MM_YYYY')}.xlsx`;
        const blob = new Blob([buffer], { type: mimeType });
        download(blob, fileName, mimeType);
        setIsDownloading(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        var data = {
            products: stockItems,
            vendorId,
            notes,
            addFees,
            ...(bazaarId ? { bazaarId } : {}),
        }

        if (requiresRefreshPassword && !refreshValidated) {
            setPendingSubmission(data);
            setRefreshDialogOpen(true);
            return;
        }

        addOutbound(data, navigate);
    };

    const addItem = (value) => {
        if(!value || value.deletedFromShopify) {
            return
        }
        setStockItems(prevItems => {
            let found = prevItems.find(item => item.stockItem === value._id);
            if (!found) {
                return [
                    ...prevItems,
                    {
                        _id: value._id,
                        sku: value.sku || value.externalId,
                        name: value.name,
                        quantity: 1,
                        stock: value.stock
                    }
                ];
            }
            return prevItems;
        });
    };

    const editItem = (idx, { target }) => {
        setStockItems(prevItems => {
            let newItems = [...prevItems];
            newItems[idx].quantity = target.value;
            return newItems;
        });
    };

    const removeItem = (id) => {
        let currItems = stockItems.filter((item) => item._id !== id)
        setStockItems(currItems)
    }

    useEffect(() => {
        getVendors({ page: 1, limit: 500 })
    }, [])

    useEffect(() => {
        const prefilled = location.state?.prefilled;
        const applyPrefilled = (p) => {
            if (!p) return;
            setNotes(p.notes || '');
            setStockItems(mapPrefilledOutboundProducts(p.products));
            setVendorId(p.vendorId);
            getProducts({ page: 1, limit: 10000, vendorId: p.vendorId, inStock: true });
        };
        if (prefilled) {
            applyPrefilled(prefilled);
        } else if (bazaarId) {
            fulfillBazaarOutbound(bazaarId).then((data) => applyPrefilled(data.prefilled));
        }
    }, [bazaarId]);

    // When products load after bazaar prefill, fill in any missing SKUs / stock from the catalog
    useEffect(() => {
        if (!bazaarId || !products?.length) {
            return;
        }
        setStockItems((prevItems) => enrichStockItemsFromProducts(prevItems, products));
    }, [bazaarId, products]);

    useEffect(() => {
        if (vendorId && !bazaarId && !location.state?.prefilled) {
            getProducts({ page: 1, limit: 10000, vendorId, inStock: true });
            setStockItems([]);
            setFile(null);
            setRefreshValidated(false);
        }
    }, [vendorId]);

    const handleRefreshClose = () => {
        if (refreshLoading) {
            return;
        }
        setRefreshDialogOpen(false);
        setRefreshPassword('');
        setRefreshError(null);
        setPendingSubmission(null);
    };

    const handleRefreshConfirm = async () => {
        if (!refreshPassword) {
            setRefreshError('Refresh password is required.');
            return;
        }
        setRefreshLoading(true);
        setRefreshError(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE}/IMAdmin/outbound/refreshPass`,
                { password: refreshPassword },
                {
                    headers: {
                        "x-access-token": token
                    },
                });

            if (response.status === 200) {
                setRefreshValidated(true);
                setRefreshDialogOpen(false);
                setRefreshPassword('');
                if (pendingSubmission) {
                    addOutbound(pendingSubmission, navigate);
                    setPendingSubmission(null);
                }
            } else {
                setRefreshError('Invalid password. Please try again.');
            }
        } catch (error) {
            setRefreshError('Unable to verify password. Please try again.');
        } finally {
            setRefreshLoading(false);
        }
    };

    return (
        <Container maxWidth="xs">
            <Box
                sx={{
                    paddingTop: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography mt={2} variant='h4' textAlign={"center"} sx={{ color: "var(--secColor)" }}>Create Outbound</Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        {vendors && <Autocomplete
                            options={vendors}
                            value={selectedVendor || null}
                            onChange={(_, value) => setVendorId(value ? value._id : null)}
                            getOptionLabel={brandOptionLabel}
                            filterOptions={brandFilterOptions}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            renderInput={(params) => <MainInput
                                {...params}
                                margin="normal"
                                fullWidth
                                label="Select Brand"
                            />}
                        />}
                        {vendorId && products && (
                            <Box sx={{ mt: 2 }}>
                                <Typography component="h2" variant="h6">
                                    Add via Excel file
                                </Typography>
                                <Tooltip title="Download a template with all in-stock products (sorted by SKU)">
                                    <span>
                                        <Button
                                            fullWidth
                                            sx={{ my: 1, ...main_button }}
                                            onClick={handleExportToExcel}
                                            disabled={isDownloading || isLoading}
                                        >
                                            <Download /> Excel sample
                                        </Button>
                                    </span>
                                </Tooltip>
                                <ReactFileReader handleFiles={handleFiles} fileTypes={['.xlsx', '.xls']}>
                                    <Button fullWidth sx={{ my: 1, ...main_button }}>
                                        <Upload /> Upload .xlsx
                                    </Button>
                                </ReactFileReader>
                                <hr />
                                <Typography component="h2" variant="h6">
                                    Add manually
                                </Typography>
                            </Box>
                        )}
                        {vendorId && products && <Autocomplete
                            options={products}
                            onChange={(_, value) => addItem(value)}
                            getOptionLabel={(product) => { return `(${product.sku || product.externalId}) ${product.name} [${product.stock?.amount ?? 0} Pcs.]${product.deletedFromShopify ? ' - Deleted from Shopify' : ''}`}}
                            getOptionDisabled={(option) => option.deletedFromShopify}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    style={option.deletedFromShopify ? { backgroundColor: '#ffe5e5', color: '#b71c1c' } : {}}
                                >
                                    {`(${option.sku || option.externalId}) ${option.name} [${option.stock?.amount ?? 0} Pcs.]${option.deletedFromShopify ? ' - Deleted from Shopify' : ''}`}
                                </li>
                            )}
                            renderInput={(params) => <MainInput
                                {...params}
                                margin="normal"
                                fullWidth
                                label="Add Products"
                            />}
                        />}
                        <hr />
                        <Grid container spacing={2}>
                            {stockItems.map((product, idx) => {
                                return (
                                    <>
                                        <Grid item xs={8} sx={{ ...left_flex_box }}>
                                            <Typography>
                                                {`(${product.sku || product.externalId}) ${product.name} [${product.stock?.amount ?? 0} Pcs.]`}
                                                {product.size && ` (${product.size})`}
                                                {product.color && ` (${product.color})`}
                                                {product.weight && ` (${product.weight})`}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <MainInput
                                                margin="normal"
                                                required
                                                fullWidth
                                                label="Quantity"
                                                type="number"
                                                inputProps={{ min: 1, max: product.stock?.amount }}
                                                value={stockItems[idx].quantity}
                                                onChange={(event) => editItem(idx, event)}
                                                autoFocus
                                            />
                                        </Grid>
                                        <Grid item xs={1} sx={{ ...centered_flex_box }}>
                                            <IconButton onClick={() => removeItem(product._id)}><Delete /></IconButton>
                                        </Grid>
                                    </>
                                )
                            })}
                            <Grid item xs={12}>
                                <MainInput
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Notes"
                                    multiline
                                    minRows={5}
                                    value={notes}
                                    onChange={({target}) => setNotes(target.value)}
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={addFees}
                                            onChange={({ target }) => setAddFees(target.checked)}
                                            sx={{ color: "var(--secColor)", "&.Mui-checked": { color: "var(--secColor)" } }}
                                        />
                                    }
                                    label="Register fee for this outbound"
                                />
                            </Grid>
                        </Grid>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, ...main_button }}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress sx={{ color: "white" }}/> : "Send Inventory Outside Chainz"}
                        </Button>
                    </Box>
                </Box>
            </Box >
            <Dialog open={refreshDialogOpen} onClose={handleRefreshClose}>
                <DialogTitle>Refresh Password Required</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>Please enter your refresh password to continue.</Typography>
                    <TextField
                        margin="normal"
                        label="Refresh Password"
                        type="password"
                        fullWidth
                        value={refreshPassword}
                        onChange={({ target }) => setRefreshPassword(target.value)}
                        autoFocus
                        error={Boolean(refreshError)}
                        helperText={refreshError}
                        disabled={refreshLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRefreshClose} disabled={refreshLoading}>Cancel</Button>
                    <Button onClick={handleRefreshConfirm} disabled={refreshLoading}>
                        {refreshLoading ? <CircularProgress size={20} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container >
    );
}

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    token: state?.auth?.token,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
    vendors: state?.records?.vendors?.docs,
    products: state?.records?.products?.docs
});

const mapDispatchToProps = { getVendors, addOutbound, getProducts, fulfillBazaarOutbound };

export default connect(mapStateToProps, mapDispatchToProps)(CreateOutbound);